import React from 'react';

export type KeyPressObject<T> = Record<string & T, boolean>;

export const useObjectKeyPress = <T,>(
  ...targetKeys: (string & T)[]
): KeyPressObject<T> => {
  const initialState = targetKeys?.reduce(
    (obj, key) => ({ ...obj, [key]: false }),
    {} as KeyPressObject<T>
  );

  const [keysPressed, setKeyPressed] = React.useState<KeyPressObject<T>>(
    initialState
  );

  const targetMatch = (...values: string[]) =>
    targetKeys?.find(key => values?.includes(key));

  const keyHandler = ({ setPressedAs = true }) => ({
    key,
    code,
  }: KeyboardEvent) => {
    const target = targetMatch(key, code);
    if (target) setKeyPressed({ ...keysPressed, [target]: setPressedAs });
  };

  React.useEffect(() => {
    const keyDownListener = keyHandler({});
    const keyUpListener = keyHandler({ setPressedAs: false });

    window.addEventListener('keydown', keyDownListener);
    window.addEventListener('keyup', keyUpListener);

    return () => {
      window.removeEventListener('keydown', keyDownListener);
      window.removeEventListener('keyup', keyUpListener);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return keysPressed;
};
